import { Box, Button, Modal, Text } from '@fozito/common-ui';
import { Form, InputRef } from 'antd';
import { delay, get, set, sumBy } from 'lodash';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { ICashDepositItem } from 'modules/POS/services/types/deposit';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useTransition } from 'react';
import { IResponseData } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import toast from 'utils/toast';
import { setPageLoading } from 'widgets/PageLoading';
import { NumberPadIntV2 } from '../../components/NumberPad';
import CashTypeItem, { CASH_DEPOSIT_CASH_TYPE, cashDepositInitialValues } from './CashTypeItem';

const FormStyled = styled(Form)``;

type Props = {};
type Ref = {
  open: () => void;
};

export const useModalCashDepositRef = () => useRef<Ref>(null);
const ModalCashDeposit = forwardRef<Ref, Props>(({ }, ref) => {
  const inputRef = useRef<InputRef>(null);
  const dispatch = useAppDispatch();
  const cashDeposits = cashierService.selectors.cashDeposits();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [KBActive, setKBActive] = useState<CASH_DEPOSIT_CASH_TYPE>(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
  const [, startTransition] = useTransition();
  const onClose = () => startTransition(() => {
    setOpen(false);
    setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
  });
  const onClear = () => {
    setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
    form.resetFields();
    delay(() => inputRef.current?.focus(), 100);
  };
  const onOpen: Ref['open'] = () => {
    if (!cashDeposits) return;
    const obj = {
      ...cashDepositInitialValues,
    };
    cashDeposits.map(o => {
      const config = get(cashDepositInitialValues, o.cashType);
      if (!config) return;
      set(obj, o.cashType, { ...config, ...o });
    });
    form.setFieldsValue(obj);
    startTransition(() => {
      setOpen(true);
      setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
    });
    delay(() => inputRef.current?.focus(), 500);
  };

  useEffect(() => {
    if (!cashDeposits) return;
    const obj = {
      ...cashDepositInitialValues,
    };
    cashDeposits.map(o => {
      const config = get(cashDepositInitialValues, o.cashType);
      if (!config) return;
      set(obj, o.cashType, { ...config, ...o });
    });
    form.setFieldsValue(obj);
  }, [cashDeposits]);

  const handleFinish = async (values: any) => {
    const data = values as Record<CASH_DEPOSIT_CASH_TYPE, ICashDepositItem>;
    setPageLoading(true);
    const body: ICashDepositItem[] = Object.entries(data).map(([key, item]) => ({
      cashType: key,
      quantity: item.quantity,
      total: (item.quantity || 0) * (item.value || 0),
    } as ICashDepositItem));

    try {
      const res: IResponseData<any> = await cashierApis.updateCashDeposit(body);
      if (res.data.data) {
        toast.success('Success');
        dispatch(cashierService.actions.async.cashDeposit.fetch());
        setOpen(false);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg ?? 'Fail!');
    } finally {
      setPageLoading(false);
    }
  };

  const onKBFocus = (type: CASH_DEPOSIT_CASH_TYPE) => () => {
    setKBActive(type);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <Modal title='CASH DEPOSIT' open={open} onClose={onClose} width={'auto'}>
      <FormStyled form={form} onFinish={handleFinish} initialValues={cashDepositInitialValues}>
        <Box display='flex' flexWrap='nowrap'>
          {Object.keys(CASH_DEPOSIT_CASH_TYPE).map((type) => (
            <Form.Item name={type} noStyle key={type}>
              <CashTypeItem
                form={form}
                inputRef={KBActive === type ? inputRef : undefined}
                type={type as CASH_DEPOSIT_CASH_TYPE}
                onFocus={onKBFocus(type as CASH_DEPOSIT_CASH_TYPE)}
              />
            </Form.Item>
          ))}
        </Box>
        <TotalBox>
          <Text title_h4_24_medium text_center>TOTAL:</Text>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldsValue }) => {
              const values = getFieldsValue();
              const arr: ICashDepositItem[] = Object.values(values);
              return <Text title_h1_56_medium text_center>{formatCurrency(sumBy(arr, o => (o?.value || 0) * o?.quantity))}</Text>;
            }}
          </Form.Item>
        </TotalBox>
        <Form.Item noStyle name={[KBActive, 'quantity']}>
          <NumberPadIntV2 controlRef={inputRef} />
        </Form.Item>
        <ModalActionsStyled>
          <Button onClick={onClose} type_cancel><span>Close</span></Button>
          <Button onClick={onClear} type_light><span>Clear</span></Button>
          <Button type_primary type='submit'><span>Ok</span></Button>
        </ModalActionsStyled>
      </FormStyled>
    </Modal>
  );
});
ModalCashDeposit.displayName = 'ModalCashDeposit';
export default ModalCashDeposit;
const ModalActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  button {
    flex: 1;
  }
  padding: 16px;
`;

const TotalBox = styled.div`
  display: flex;
  margin-top: 8px;
  padding: 0px 40px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: 16px;
  border-bottom: 1px solid #86909C;
  background: #E8FBFF;
`;